<script setup lang="ts">
/**
 * Base wrapper around PrimeVue's DataTable, to keep things centrally controllable.
 * It is meant to be used with a BaseCollection and uses reactive variables
 * for sorting, paging etc.
 *
 * More slots and props are added as needed. Please use this component instead of using
 * PrimeVue's DataTable directly.
 */
import { computed } from 'vue';
import { type DataTablePageEvent, type DataTableSortEvent, type DataTableSortMeta } from 'primevue/datatable';
import type { SortOption } from '@/composables/managedCollection';
import type { BaseCollection } from '@/models/BaseCollection';
import type { BaseModel } from '@/models/BaseModel';
import { instance as api, buildUrl } from '@/helpers/api';
import KpBaseGrid from './KpBaseGrid.vue';

export interface KpBaseCollectionGridProps {
    // a BaseCollection is needed:
    collection: BaseCollection<BaseModel>;
    rowsPerPageOptions?: number[];
}

const props = withDefaults(defineProps<KpBaseCollectionGridProps>(), {
    rowsPerPageOptions: () => [20, 50, 100, 250],
});

// Multisort table model, communicates the sort direction to the outer world:
const tableSort = defineModel<SortOption[]>('tableSort', { default: [] });

// Paging model
const pageSize = defineModel<number>('pageSize', { default: 250 });
const page = defineModel<number>('page', { default: 1 });

// convert SortOption array to DataTableSortMeta array, as needed by the PrimeVue Base table:
const multiSortMeta = computed(() => {
    const meta: DataTableSortMeta[] = [];
    tableSort.value.forEach((sortEntry) => {
        meta.push({ field: sortEntry.property, order: sortEntry.direction === 'desc' ? -1 : 1 });
    });
    return meta;
});

function onReloadBtn() {
    props.collection.query();
}

async function onCsvDownload() {
    const params = props.collection.queryParams;
    params.output = 'csv';
    const url = buildUrl('/' + props.collection.getModelClassName() + '/list', params);
    window.open(url, 'csvExport');
}

function onSort(data: DataTableSortEvent) {
    // convert PrimeVue's sort meta array to SortOption array, as needed by our framework:
    const sortData: SortOption[] = [];
    data.multiSortMeta?.forEach((metaEntry) => {
        sortData.push({ property: String(metaEntry.field), direction: metaEntry.order === -1 ? 'desc' : 'asc' });
    });
    // and update the sort model:
    tableSort.value = sortData;
}

function onPage(data: DataTablePageEvent) {
    pageSize.value = data.rows;
    page.value = Math.floor(data.first / data.rows) + 1;
}
</script>

<template>
    <KpBaseGrid
        :value="collection.getModels()"
        :loading="collection.loading.value"
        :rows="pageSize"
        :first="(page - 1) * pageSize"
        :rowsPerPageOptions="rowsPerPageOptions"
        paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
        :currentPageReportTemplate="$t('paginator_current_page_report')"
        lazy
        sort-mode="multiple"
        :multi-sort-meta="multiSortMeta"
        @sort="onSort"
        @page="onPage"
        @reload="onReloadBtn"
        @csvdownload="onCsvDownload"
    >
        <template #groupheader="slotProps">
            <slot name="groupheader" :data="slotProps.data" :index="slotProps.index"></slot>
        </template>
        <!-- default slot, passes columns etc. to the inner Base Grid -->
        <slot></slot>
    </KpBaseGrid>
</template>
